import React, { useEffect, useMemo, useState } from "react";
import { translateUi } from './i18n/index';
import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  AlertTriangle,
  Bell,
  Calendar,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  Clock,
  FileCheck,
  MessageCircle,
  Play,
  ShieldCheck,
  Users,
} from "./icons";
import { TeamReporteeVerificationModal, type VerificationFixTarget } from "./QAVerificationReport";
import { preferWideDashboardPanes, useDeviceType } from "./useDeviceType";

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export type DashboardInboxItem = {
  id: number | string;
  title: string;
  message: string;
  type: string;
  created_at?: string;
  employee_id?: string;
  note_ref?: string;
  company_code?: string;
  task_id?: string;
  incident_id?: string;
};

type TaskLite = {
  id: string;
  title: string;
  status?: string;
  due_date?: string | null;
  assignee_email?: string;
  created_at?: string;
  updated_at?: string;
};

type LeaveRequestLite = {
  id: string;
  employee_id?: string;
  employee_name?: string;
  status?: string;
  type?: string;
  start_date?: string;
  end_date?: string;
  assessing_manager_email?: string;
  requested_at?: string;
};

type LeaveBalanceLite = {
  employee_id: string;
  employee_name?: string;
  annual_days: number;
  remaining_days: number;
  pending_days: number;
  used_days: number;
  /** Present when annual entitlement has been set for this employee. */
  entitlement_set_at?: string | null;
};

type ActionRow = {
  id: string;
  kind: "task" | "leave" | "reminder" | "setup" | "qa" | "message";
  title: string;
  detail: string;
  urgency: "critical" | "attention" | "info";
  onClick: () => void;
};

type UnreadMessage = {
  id: string;
  noteId: string;
  employeeId: string;
  employeeName: string;
  authorName: string;
  preview: string;
  createdAt: string;
};

type QaGlanceIssue = {
  employeeId: string;
  employeeName: string;
  employeeRole?: string;
  employeeDepot?: string;
  runId: string;
  runAt: string;
  overall: string;
  reviewCount: number;
  advisoryCount: number;
  nonComplianceCount: number;
};

type QaGlanceData = {
  allowed: boolean;
  source?: "live";
  checkedAt?: string;
  scope: "team" | "company";
  teamSize: number;
  reporteeCount?: number;
  employeeCount: number;
  outcomes: { pass: number; advisory: number; fail: number };
  counts: { C: number; A: number; R: number; NA: number };
  issues: QaGlanceIssue[];
  issueCount: number;
};

type TeamSummaryData = {
  totalEmployees?: number;
  totalActive?: number;
  overdueComp?: number;
  dueSoonComp?: number;
  expiredMed?: number;
  dueSoonMed?: number;
  offTrackCount?: number;
  complianceRate?: number | null;
  competencyInDateRate?: number | null;
  licensing?: {
    expired?: number;
    dueSoonSixWeeks?: number;
    dueSoonThreeMonths?: number;
  };
};

function emailMatch(a?: string | null, b?: string | null): boolean {
  return String(a || "")
    .trim()
    .toLowerCase() ===
    String(b || "")
      .trim()
      .toLowerCase();
}

function taskIsDone(t: TaskLite): boolean {
  return (t.status || "open").trim().toLowerCase() === "done";
}

function formatShortDate(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

function leaveTypeLabel(type?: string): string {
  const t = String(type || "").trim().toLowerCase();
  if (!t) return translateUi("Leave");
  return t
    .split(/[_-]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function reminderKindLabel(type: string): string {
  switch (type) {
    case "task_assigned":
    case "task_reassigned":
    case "task_reopened":
    case "task_note_added":
      return translateUi("Task");
    case "employee_note":
      return translateUi("Note");
    case "fatality_anniversary_7d":
      return translateUi("Reminder");
    case "licence_expiry_3m":
    case "licence_expiry_6w":
      return translateUi("Licence reminder");
    case "licence_expiry_expired":
      return translateUi("Licence expired");
    case "critical":
      return translateUi("Critical");
    case "warning":
      return translateUi("Warning");
    default:
      return translateUi("Update");
  }
}

const DONUT_SIZE = 176;
const DONUT_STROKE = 14;

function useDonutDrawn() {
  const [drawn, setDrawn] = useState(false);
  useEffect(() => {
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
    if (reduce) {
      setDrawn(true);
      return;
    }
    const id = window.requestAnimationFrame(() => setDrawn(true));
    return () => window.cancelAnimationFrame(id);
  }, []);
  return drawn;
}

type DonutSlice = { key: string; n: number; color: string; label: string };

/** Shared multi-slice donut (same size for personal + team panes). */
function SliceDonut({
  slices,
  centerValue,
  centerHint,
  centerTone,
  isDarkMode,
}: {
  slices: DonutSlice[];
  centerValue: string | number;
  centerHint: string;
  centerTone: "clear" | "busy";
  isDarkMode: boolean;
}) {
  const size = DONUT_SIZE;
  const stroke = DONUT_STROKE;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const total = slices.reduce((s, x) => s + x.n, 0);
  const drawn = useDonutDrawn();

  let cursor = 0;
  const arcs =
    total > 0
      ? slices
          .filter((s) => s.n > 0)
          .map((s) => {
            const len = (s.n / total) * circumference;
            const start = cursor;
            cursor += len;
            return { ...s, len, start };
          })
      : [];

  const clearColor = isDarkMode ? "#34d399" : "#10b981";

  return (
    <div className="flex h-full min-h-0 w-full flex-col items-center justify-center gap-5 py-1">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90" aria-hidden>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={r}
            fill="none"
            stroke={isDarkMode ? "rgba(148,163,184,0.22)" : "rgba(148,163,184,0.35)"}
            strokeWidth={stroke}
          />
          {total === 0 ? (
            <circle
              cx={size / 2}
              cy={size / 2}
              r={r}
              fill="none"
              stroke={clearColor}
              strokeWidth={stroke}
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={drawn ? 0 : circumference}
              className="rv-progress-donut-arc"
            />
          ) : (
            arcs.map((a) => (
              <circle
                key={a.key}
                cx={size / 2}
                cy={size / 2}
                r={r}
                fill="none"
                stroke={a.color}
                strokeWidth={stroke}
                strokeLinecap="butt"
                strokeDasharray={`${a.len} ${circumference}`}
                strokeDashoffset={drawn ? -a.start : circumference}
                className="rv-progress-donut-arc"
              />
            ))
          )}
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-3">
          <span
            className={cn(
              "text-3xl font-bold tabular-nums leading-none sm:text-4xl",
              centerTone === "busy"
                ? isDarkMode
                  ? "text-rose-300"
                  : "text-rose-600"
                : isDarkMode
                  ? "text-emerald-300"
                  : "text-emerald-600"
            )}
          >
            {centerValue}
          </span>
          <span className={cn("mt-1.5 text-[11px] font-medium", isDarkMode ? "text-slate-400" : "text-slate-500")}>
            {centerHint}
          </span>
        </div>
      </div>
      <ul className="w-full max-w-[15rem] space-y-2">
        {slices.map((s) => (
          <li key={s.key} className="flex min-w-0 items-center gap-2.5 text-sm">
            <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
            <span className={cn("min-w-0 flex-1 truncate font-medium", isDarkMode ? "text-slate-300" : "text-slate-600")}>
              {s.label}
            </span>
            <span className={cn("shrink-0 tabular-nums font-bold", isDarkMode ? "text-slate-100" : "text-slate-900")}>
              {s.n}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Join category labels for My progress copy (e.g. "messages, tasks and reminders"). */
function joinPersonalCategories(parts: string[], finalWord: "and" | "or" = "and"): string {
  const conj = translateUi(finalWord);
  if (parts.length === 0) return translateUi("items");
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} ${conj} ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, ${conj} ${parts[parts.length - 1]}`;
}

function PersonalAttentionDonut({
  tasks,
  approvals,
  reminders,
  messages,
  setup,
  showTasks,
  showApprovals,
  isDarkMode,
}: {
  tasks: number;
  approvals: number;
  reminders: number;
  messages: number;
  setup: number;
  /** Task Assignment add-on active. */
  showTasks: boolean;
  /** Leave & Absence add-on active (holiday approvals). */
  showApprovals: boolean;
  isDarkMode: boolean;
}) {
  const slices: DonutSlice[] = [
    { key: "messages", n: messages, color: isDarkMode ? "#38bdf8" : "#0ea5e9", label: translateUi("Messages") },
  ];
  if (showTasks) {
    slices.push({ key: "tasks", n: tasks, color: isDarkMode ? "#818cf8" : "#6366f1", label: translateUi("Tasks") });
  }
  if (showApprovals) {
    slices.push({
      key: "approvals",
      n: approvals,
      color: isDarkMode ? "#fbbf24" : "#f59e0b",
      label: translateUi("Approvals"),
    });
  }
  slices.push({
    key: "reminders",
    n: reminders,
    color: isDarkMode ? "#a78bfa" : "#8b5cf6",
    label: translateUi("Reminders"),
  });
  if (setup > 0) {
    slices.push({
      key: "setup",
      n: setup,
      color: isDarkMode ? "#fb7185" : "#ef4444",
      label: translateUi("Setup"),
    });
  }
  const taskN = showTasks ? tasks : 0;
  const approvalN = showApprovals ? approvals : 0;
  const openTotal = taskN + approvalN + reminders + messages + setup;
  return (
    <SliceDonut
      slices={slices}
      centerValue={openTotal}
      centerHint={
        openTotal === 0
          ? translateUi("all clear")
          : openTotal === 1
            ? translateUi("open item")
            : translateUi("open items")
      }
      centerTone={openTotal > 0 ? "busy" : "clear"}
      isDarkMode={isDarkMode}
    />
  );
}

/** Multi-slice severity donut matching QA verification colours (C / A / R). */
function QaSeverityDonut({
  counts,
  isDarkMode,
}: {
  counts: { C: number; A: number; R: number; NA: number };
  isDarkMode: boolean;
}) {
  const slices: DonutSlice[] = [
    { key: "R", n: counts.R, color: isDarkMode ? "#fb7185" : "#ef4444", label: translateUi("Review") },
    { key: "A", n: counts.A, color: isDarkMode ? "#fbbf24" : "#f59e0b", label: translateUi("Advisory") },
    { key: "C", n: counts.C, color: isDarkMode ? "#34d399" : "#10b981", label: translateUi("Compliant") },
  ];
  const issueTotal = counts.R + counts.A;
  return (
    <SliceDonut
      slices={slices}
      centerValue={issueTotal}
      centerHint={translateUi("live issues")}
      centerTone={issueTotal > 0 ? "busy" : "clear"}
      isDarkMode={isDarkMode}
    />
  );
}

function ActionItemButton({
  row,
  isDarkMode,
}: {
  row: ActionRow;
  isDarkMode: boolean;
}) {
  const tone =
    row.urgency === "critical"
      ? isDarkMode
        ? "border-rose-500/40 bg-rose-950/40 hover:bg-rose-950/60"
        : "border-rose-200 bg-rose-50/80 hover:bg-rose-50"
      : row.urgency === "attention"
        ? isDarkMode
          ? "border-amber-500/35 bg-amber-950/30 hover:bg-amber-950/50"
          : "border-amber-200 bg-amber-50/70 hover:bg-amber-50"
        : isDarkMode
          ? "border-slate-600 bg-slate-800/60 hover:bg-slate-800"
          : "border-slate-200 bg-slate-50/80 hover:bg-slate-50";

  const Icon =
    row.kind === "task"
      ? ClipboardList
      : row.kind === "leave"
        ? Calendar
        : row.kind === "setup"
          ? AlertTriangle
          : row.kind === "qa"
            ? FileCheck
            : row.kind === "message"
              ? MessageCircle
              : Bell;

  const iconWrap =
    row.urgency === "critical"
      ? "bg-rose-600 text-white"
      : row.urgency === "attention"
        ? "bg-amber-500 text-white"
        : row.kind === "qa"
          ? "bg-teal-600 text-white"
          : row.kind === "message"
            ? "bg-sky-600 text-white"
            : "bg-slate-700 text-white";

  return (
    <button
      type="button"
      onClick={row.onClick}
      className={cn(
        "group w-full text-left rounded-xl border px-3.5 py-3 transition-colors flex items-start gap-3",
        tone
      )}
    >
      <span className={cn("mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg", iconWrap)}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className={cn("block text-sm font-semibold truncate", isDarkMode ? "text-slate-50" : "text-slate-900")}>
          {row.title}
        </span>
        <span className={cn("block text-xs mt-0.5 line-clamp-2", isDarkMode ? "text-slate-400" : "text-slate-500")}>
          {row.detail}
        </span>
      </span>
      <ChevronRight
        className={cn(
          "w-4 h-4 shrink-0 mt-2 transition-transform group-hover:translate-x-0.5",
          isDarkMode ? "text-slate-500" : "text-slate-400"
        )}
      />
    </button>
  );
}

export function DashboardForYouPanel({
  isDarkMode,
  isMobileOrTablet,
  companyCode,
  currentUserEmail,
  companyAddons,
  onTab,
  inboxItems,
  onInboxItemClick,
  setupIssues,
  onOpenSetup,
  refreshTrigger,
  onOpenEmployee,
  canRunQAVerifications = false,
  currentUserName,
  onNavigateToQaFix,
}: {
  isDarkMode: boolean;
  isMobileOrTablet?: boolean;
  companyCode?: string;
  currentUserEmail?: string;
  companyAddons: string[];
  onTab: (tab: string) => void;
  inboxItems: DashboardInboxItem[];
  onInboxItemClick: (item: DashboardInboxItem) => void;
  setupIssues?: string[] | null;
  onOpenSetup?: () => void;
  refreshTrigger?: number;
  onOpenEmployee?: (employeeId: string) => void;
  canRunQAVerifications?: boolean;
  currentUserName?: string;
  onNavigateToQaFix?: (target: VerificationFixTarget & { employeeId: string }) => void;
}) {
  const { isTablet, isLandscape } = useDeviceType();
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1024;
  const wideDashboardPanes = preferWideDashboardPanes(
    !!isMobileOrTablet,
    isTablet,
    isLandscape,
    viewportWidth
  );
  const taskAddon = companyAddons.includes("task-assignment");
  const leaveAddon = companyAddons.includes("leave-absence");
  const qaAddon = companyAddons.includes("qa-verifications");
  const me = (currentUserEmail || "").trim();

  const [myTasks, setMyTasks] = useState<TaskLite[] | null>(null);
  const [leavePending, setLeavePending] = useState<LeaveRequestLite[] | null>(null);
  const [myLeaveBalance, setMyLeaveBalance] = useState<LeaveBalanceLite | null>(null);
  const [missingLeaveEntitlements, setMissingLeaveEntitlements] = useState<
    { employee_id: string; employee_name?: string }[]
  >([]);
  const [leaveLoaded, setLeaveLoaded] = useState(!leaveAddon);
  const [tasksLoaded, setTasksLoaded] = useState(!taskAddon);
  const [qaGlance, setQaGlance] = useState<QaGlanceData | null>(null);
  const [qaLoaded, setQaLoaded] = useState(!qaAddon);
  const [qaRefreshKey, setQaRefreshKey] = useState(0);
  const [teamQaOpen, setTeamQaOpen] = useState(false);
  const [teamSummary, setTeamSummary] = useState<TeamSummaryData | null>(null);
  const [teamSummaryLoaded, setTeamSummaryLoaded] = useState(!companyCode || !me);
  const [unreadMessages, setUnreadMessages] = useState<UnreadMessage[]>([]);
  const [messagesLoaded, setMessagesLoaded] = useState(false);

  useEffect(() => {
    if (!companyCode?.trim() || !me) {
      setUnreadMessages([]);
      setMessagesLoaded(true);
      return;
    }
    let cancelled = false;
    setMessagesLoaded(false);
    fetch(`/api/companies/${encodeURIComponent(companyCode.trim())}/messaging/unread-for-me`, {
      cache: "no-store",
      headers: {
        "X-User-Email": me,
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { messages?: UnreadMessage[] } | null) => {
        if (cancelled) return;
        setUnreadMessages(Array.isArray(d?.messages) ? d!.messages! : []);
        setMessagesLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setUnreadMessages([]);
          setMessagesLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, me, refreshTrigger]);

  useEffect(() => {
    if (!taskAddon || !companyCode?.trim() || !me) {
      setMyTasks(null);
      setTasksLoaded(true);
      return;
    }
    let cancelled = false;
    setTasksLoaded(false);
    fetch(`/api/tasks?companyCode=${encodeURIComponent(companyCode.trim())}`, {
      headers: {
        "X-User-Email": me,
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { tasks?: TaskLite[] } | null) => {
        if (cancelled) return;
        const rows = Array.isArray(d?.tasks) ? d!.tasks! : [];
        setMyTasks(rows.filter((t) => emailMatch(t.assignee_email, me)));
        setTasksLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setMyTasks([]);
          setTasksLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [taskAddon, companyCode, me, refreshTrigger]);

  useEffect(() => {
    if (!leaveAddon || !companyCode?.trim() || !me) {
      setLeavePending(null);
      setMyLeaveBalance(null);
      setMissingLeaveEntitlements([]);
      setLeaveLoaded(true);
      return;
    }
    let cancelled = false;
    setLeaveLoaded(false);
    fetch(`/api/leave-absence/summary?companyCode=${encodeURIComponent(companyCode.trim())}`, {
      headers: {
        "X-User-Email": me,
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(
        (
          d: {
            requests?: LeaveRequestLite[];
            balances?: LeaveBalanceLite[];
            myEmployeeIds?: string[];
            canApprove?: boolean;
            canManage?: boolean;
          } | null,
        ) => {
          if (cancelled) return;
          const myIds = new Set((d?.myEmployeeIds || []).map(String));
          const canAct = Boolean(d?.canApprove || d?.canManage);
          const nameById = new Map(
            (d?.balances || []).map((b) => [String(b.employee_id), String(b.employee_name || "").trim()]),
          );
          const pending = (d?.requests || []).filter((r) => String(r.status || "").toLowerCase() === "pending");
          const awaitingMe = canAct
            ? pending
                .filter((r) => {
                  const empId = String(r.employee_id || "");
                  if (myIds.has(empId)) return false;
                  return true;
                })
                .map((r) => ({
                  ...r,
                  employee_name:
                    (r.employee_name || "").trim() || nameById.get(String(r.employee_id || "")) || undefined,
                }))
            : [];
          setLeavePending(awaitingMe);
          const mine = (d?.balances || []).find((b) => myIds.has(String(b.employee_id)));
          setMyLeaveBalance(mine || null);
          // Managers/admins: remind when reportees (or company) still need holiday entitlement set.
          const missing = canAct
            ? (d?.balances || [])
                .filter((b) => {
                  const id = String(b.employee_id || "");
                  if (!id || myIds.has(id)) return false;
                  return !b.entitlement_set_at;
                })
                .map((b) => ({
                  employee_id: String(b.employee_id),
                  employee_name: String(b.employee_name || "").trim() || undefined,
                }))
            : [];
          setMissingLeaveEntitlements(missing);
          setLeaveLoaded(true);
        },
      )
      .catch(() => {
        if (!cancelled) {
          setLeavePending([]);
          setMyLeaveBalance(null);
          setMissingLeaveEntitlements([]);
          setLeaveLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [leaveAddon, companyCode, me, refreshTrigger]);

  useEffect(() => {
    if (!qaAddon || !companyCode?.trim() || !me) {
      setQaGlance(null);
      setQaLoaded(true);
      return;
    }
    let cancelled = false;
    setQaLoaded(false);
    fetch(`/api/companies/${encodeURIComponent(companyCode.trim())}/verification/dashboard-glance`, {
      cache: "no-store",
      headers: {
        "X-User-Email": me,
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then(async (r) => {
        if (r.status === 403) return null;
        if (!r.ok) return null;
        return r.json() as Promise<QaGlanceData>;
      })
      .then((d) => {
        if (cancelled) return;
        setQaGlance(d?.allowed ? d : null);
        setQaLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setQaGlance(null);
          setQaLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [qaAddon, companyCode, me, refreshTrigger, qaRefreshKey]);

  useEffect(() => {
    if (!companyCode?.trim() || !me) {
      setTeamSummary(null);
      setTeamSummaryLoaded(true);
      return;
    }
    let cancelled = false;
    setTeamSummaryLoaded(false);
    const params = new URLSearchParams({
      companyCode: companyCode.trim(),
      manager: String(currentUserName ?? "").trim(),
      managerEmail: me,
    });
    fetch(`/api/stats?${params.toString()}`, {
      cache: "no-store",
      headers: {
        "X-User-Email": me,
        "X-Company-Code": companyCode.trim(),
      },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: TeamSummaryData | null) => {
        if (cancelled) return;
        setTeamSummary(data && typeof data === "object" ? data : null);
        setTeamSummaryLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setTeamSummary(null);
          setTeamSummaryLoaded(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyCode, currentUserName, me, refreshTrigger]);

  const openTasks = useMemo(
    () => (myTasks || []).filter((t) => !taskIsDone(t)),
    [myTasks],
  );
  const doneTasks = useMemo(
    () => (myTasks || []).filter((t) => taskIsDone(t)),
    [myTasks],
  );
  const taskTotal = openTasks.length + doneTasks.length;
  const taskProgress = taskTotal > 0 ? doneTasks.length / taskTotal : 1;

  const leaveAnnual = Math.max(0, Number(myLeaveBalance?.annual_days ?? 0) || 0);
  const leaveRemaining = Math.max(0, Number(myLeaveBalance?.remaining_days ?? 0) || 0);

  const reminders = useMemo(() => {
    const rows = inboxItems || [];
    // Messages have their own unread list; skip employee_note inbox rows to avoid duplicates.
    // Hide add-on inbox kinds when that add-on is not active; when Tasks is active and open
    // tasks are listed as action rows, skip task_* kinds to avoid duplicates.
    return rows.filter((a) => {
      if (a.type === "employee_note" || a.type === "employee_note_read") return false;
      const isTaskKind =
        a.type === "task_assigned" ||
        a.type === "task_reassigned" ||
        a.type === "task_reopened" ||
        a.type === "task_note_added";
      if (isTaskKind) {
        if (!taskAddon) return false;
        if (openTasks.length > 0) return false;
        return true;
      }
      if (String(a.type || "").startsWith("leave_request_") && !leaveAddon) return false;
      return true;
    });
  }, [inboxItems, taskAddon, leaveAddon, openTasks.length]);
  const pendingLeaveCount = leaveAddon ? leavePending?.length ?? 0 : 0;
  const missingEntitlementCount = leaveAddon ? missingLeaveEntitlements.length : 0;
  const setupCount = setupIssues?.length ?? 0;
  const messageCount = unreadMessages.length;
  /** Missing leave entitlements surface as a My progress reminder when Leave & Absence is active. */
  const reminderCount = reminders.length + (missingEntitlementCount > 0 ? 1 : 0);
  const actionLoad =
    (taskAddon ? openTasks.length : 0) +
    pendingLeaveCount +
    reminderCount +
    setupCount +
    messageCount;
  const personalCategoryLabels = useMemo(() => {
    const parts = [translateUi("messages")];
    if (taskAddon) parts.push(translateUi("tasks"));
    if (leaveAddon) parts.push(translateUi("approvals"));
    parts.push(translateUi("reminders"));
    return parts;
  }, [taskAddon, leaveAddon]);
  const personalCategoriesJoined = joinPersonalCategories(personalCategoryLabels);
  const personalCategoriesOrJoined = joinPersonalCategories(personalCategoryLabels, "or");
  const moreListHint = useMemo(() => {
    const places = [translateUi("Messaging & Notes")];
    if (taskAddon) places.push(translateUi("Tasks"));
    if (leaveAddon) places.push(translateUi("Leave"));
    places.push(translateUi("the bell"));
    return translateUi("Showing the most urgent items. Open {{places}} for the full list.", {
      places: joinPersonalCategories(places),
    });
  }, [taskAddon, leaveAddon]);

  const actions = useMemo((): ActionRow[] => {
    const rows: ActionRow[] = [];

    if (setupCount > 0 && onOpenSetup && setupIssues) {
      rows.push({
        id: "setup",
        kind: "setup",
        title: translateUi("Finish company setup"),
        detail: setupIssues.slice(0, 3).join(" · "),
        urgency: "attention",
        onClick: onOpenSetup,
      });
    }

    for (const msg of unreadMessages.slice(0, 12)) {
      const when = formatShortDate(msg.createdAt);
      const messageTitle = translateUi("Message from {{name}}", { name: msg.authorName });
      rows.push({
        id: `message-${msg.id}`,
        kind: "message",
        title: messageTitle,
        detail: [msg.employeeName, msg.preview, when].filter(Boolean).join(" · "),
        urgency: "attention",
        onClick: () =>
          onInboxItemClick({
            id: `msg-${msg.id}`,
            title: messageTitle,
            message: msg.preview,
            type: "employee_note",
            employee_id: msg.employeeId,
            note_ref: msg.noteId,
            company_code: companyCode,
            created_at: msg.createdAt,
          }),
      });
    }

    for (const t of openTasks.slice(0, 12)) {
      if (!taskAddon) break;
      const due = formatShortDate(t.due_date);
      const overdue =
        t.due_date && Number.isFinite(new Date(t.due_date).getTime())
          ? new Date(t.due_date).getTime() < Date.now() - 86400000
          : false;
      rows.push({
        id: `task-${t.id}`,
        kind: "task",
        title: t.title || "Open task",
        detail: due
          ? overdue
            ? translateUi("Overdue · due {{date}}", { date: due })
            : translateUi("Due {{date}}", { date: due })
          : translateUi("Assigned to you"),
        urgency: overdue ? "critical" : "attention",
        onClick: () => onTab("tasks"),
      });
    }

    for (const r of (leaveAddon ? leavePending || [] : []).slice(0, 8)) {
      const who = (r.employee_name || "").trim() || "Team member";
      const range =
        [formatShortDate(r.start_date), formatShortDate(r.end_date)].filter(Boolean).join(" – ") ||
        "Dates TBC";
      rows.push({
        id: `leave-${r.id}`,
        kind: "leave",
        title: translateUi("Approve {{type}}", { type: leaveTypeLabel(r.type) }),
        detail: `${who} · ${range}`,
        urgency: "attention",
        onClick: () => onTab("leave-absence"),
      });
    }

    if (leaveAddon && missingEntitlementCount > 0) {
      const sample = missingLeaveEntitlements
        .slice(0, 3)
        .map((e) => e.employee_name || "Employee")
        .join(" · ");
      rows.push({
        id: "leave-entitlements-missing",
        kind: "reminder",
        title:
          missingEntitlementCount === 1
            ? translateUi("Set holiday entitlement")
            : translateUi("Set holiday entitlements ({{count}})", { count: missingEntitlementCount }),
        detail:
          missingEntitlementCount === 1
            ? translateUi("{{name}} has no annual leave entitlement set", { name: sample })
            : translateUi("{{names}} — open Leave & Absence → Entitlements", {
                names: `${sample}${missingEntitlementCount > 3 ? "…" : ""}`,
              }),
        urgency: "attention",
        onClick: () => onTab("leave-absence"),
      });
    }

    for (const a of reminders.slice(0, 12)) {
      const tone =
        a.type === "critical" ||
        a.type === "fatality_anniversary_7d" ||
        a.type === "licence_expiry_expired"
          ? "critical"
          : a.type === "warning" ||
              a.type === "licence_expiry_3m" ||
              a.type === "licence_expiry_6w"
            ? "attention"
            : "info";
      rows.push({
        id: `inbox-${a.id}`,
        kind: "reminder",
        title: a.title || reminderKindLabel(a.type),
        detail: a.message || reminderKindLabel(a.type),
        urgency: tone,
        onClick: () => onInboxItemClick(a),
      });
    }

    return rows;
  }, [
    setupCount,
    setupIssues,
    onOpenSetup,
    unreadMessages,
    openTasks,
    leavePending,
    missingLeaveEntitlements,
    missingEntitlementCount,
    reminders,
    taskAddon,
    leaveAddon,
    onTab,
    onInboxItemClick,
    companyCode,
  ]);

  const openQaEmployee = (employeeId: string) => {
    if (onOpenEmployee && employeeId) {
      onOpenEmployee(employeeId);
      return;
    }
    onTab("qa-verifications");
  };

  const showQaSection = Boolean(qaAddon && qaLoaded && qaGlance?.allowed);
  const showManagerTeam = Boolean(teamSummaryLoaded && (teamSummary?.totalEmployees ?? 0) > 0);
  const teamComplianceRate =
    teamSummary?.competencyInDateRate != null
      ? teamSummary.competencyInDateRate
      : teamSummary?.complianceRate != null
        ? teamSummary.complianceRate
        : null;
  const loaded = tasksLoaded && leaveLoaded && messagesLoaded;
  const showLeaveMeta = leaveAddon && leaveLoaded && myLeaveBalance != null && leaveAnnual > 0;
  const showTaskMeta = taskAddon && tasksLoaded && taskTotal > 0;
  const hasPersonalSurface =
    taskAddon ||
    leaveAddon ||
    reminders.length > 0 ||
    messageCount > 0 ||
    setupCount > 0 ||
    showQaSection ||
    showManagerTeam ||
    (qaAddon && !qaLoaded);

  if (!hasPersonalSurface) return null;

  const qaScopeLabel =
    qaGlance?.scope === "company"
      ? translateUi("Live company records")
      : translateUi("Live records for your reportees");
  const reporteeCount = qaGlance?.reporteeCount ?? 0;
  const paneShell =
    "relative flex h-full min-h-[22rem] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-700/80 bg-gradient-to-br from-slate-900/88 via-indigo-950/82 to-slate-900/88 p-5 text-white shadow-xl backdrop-blur-[2px] sm:min-h-[26rem] sm:p-6";
  const paneGlowA =
    "pointer-events-none absolute -right-16 -top-20 h-56 w-56 rounded-full bg-sky-400/15 blur-3xl";
  const paneGlowB =
    "pointer-events-none absolute -bottom-24 -left-12 h-52 w-52 rounded-full bg-indigo-500/20 blur-3xl";
  const innerPane =
    "flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-xl border border-white/15 bg-white/[0.06] p-4 backdrop-blur-sm sm:p-5";
  const innerGrid = cn(
    "grid flex-1 gap-5 min-h-0",
    wideDashboardPanes ? "grid-cols-2" : "grid-cols-1"
  );
  const scrollList =
    "flex min-h-0 min-w-0 flex-1 flex-col gap-2.5 overflow-y-auto overscroll-contain pe-0.5 [scrollbar-gutter:stable]";

  return (
    <>
      <div
        className={cn(
          // Grow with the dashboard flex column so panes fill leftover viewport height.
          "grid h-full min-h-[22rem] flex-1 items-stretch gap-5 sm:min-h-[26rem] lg:gap-6",
          (showQaSection || showManagerTeam) && wideDashboardPanes ? "grid-cols-2" : "grid-cols-1"
        )}
      >
      <section className={paneShell}>
        <div className={paneGlowA} aria-hidden />
        <div className={paneGlowB} aria-hidden />
        <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-indigo-100 ring-1 ring-white/20">
                  <ClipboardList className="h-5 w-5" />
                </span>
                <div>
                  <h2 className="text-base font-bold tracking-tight text-white">{translateUi("My progress")}</h2>
                  <p className="mt-0.5 text-xs text-slate-300">
                    {translateUi("Your personal {{categories}}", { categories: personalCategoriesJoined })}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {loaded && actionLoad > 0 ? (
                <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/20 bg-amber-400/15 px-2.5 py-1 text-[11px] font-semibold text-amber-200 tabular-nums">
                  <Clock className="h-3.5 w-3.5" />
                  {actionLoad} {translateUi("open")}
                </span> ) : loaded ? ( <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden /> {translateUi("Clear")} </span>
              ) : null}
            </div>
          </div>

          <div className={innerGrid}>
            <div className={innerPane}>
              <div className="flex min-h-0 flex-1 flex-col">
                {loaded ? (
                  <PersonalAttentionDonut
                    tasks={openTasks.length}
                    approvals={pendingLeaveCount}
                    reminders={reminderCount}
                    messages={messageCount}
                    setup={setupCount}
                    showTasks={taskAddon}
                    showApprovals={leaveAddon}
                    isDarkMode
                  />
                ) : (
                  <div className="flex min-h-[8rem] flex-1 items-center justify-center text-sm text-slate-400">
                    {translateUi("Loading…")}
                  </div>
                )}
              </div>
              <div className="mt-3 flex shrink-0 flex-wrap justify-center gap-2 border-t border-white/10 pt-3 text-sm font-semibold text-slate-300">
                {showTaskMeta ? (
                  <>
                    <span className="text-indigo-200">
                      {translateUi("Tasks {{pct}}% done", { pct: Math.round(taskProgress * 100) })}
                    </span>
                    {(showLeaveMeta || setupCount > 0) && <span aria-hidden>·</span>}
                  </>
                ) : null}
                {showLeaveMeta ? (
                  <>
                    <span className="text-teal-200">
                      {translateUi("Leave {{remaining}}/{{annual}} days", {
                        remaining: Math.round(leaveRemaining * 10) / 10,
                        annual: leaveAnnual,
                      })}
                    </span>
                    {setupCount > 0 && <span aria-hidden>·</span>}
                  </>
                ) : null}
                {setupCount > 0 ? (
                  <span className="text-rose-300">{translateUi("Setup {{count}}", { count: setupCount })}</span>
                ) : !showTaskMeta && !showLeaveMeta ? (
                  <span>
                    {actionLoad === 0
                      ? translateUi("Nothing waiting on you")
                      : translateUi("{{count}} needing attention", { count: actionLoad })}
                  </span>
                ) : null}
              </div>
            </div>

            <div className={scrollList}>
              {!loaded ? (
                <div className="rounded-xl border border-dashed border-slate-600 bg-white/[0.03] px-4 py-8 text-center text-sm text-slate-400">
                  {translateUi("Loading your actions…")}
                </div>
              ) : actions.length === 0 ? (
                <div className="flex h-full min-h-[8rem] items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-950/20 px-4 py-5">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />
                  <div>
                    <p className="text-sm font-semibold text-emerald-100">{translateUi("You're all caught up")}</p>
                    <p className="mt-1 text-xs text-emerald-200/80">
                      {translateUi("No personal {{categories}} waiting on you right now.", {
                        categories: personalCategoriesOrJoined,
                      })}
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {actions.map((row) => (
                    <ActionItemButton key={row.id} row={row} isDarkMode />
                  ))}
                  {(openTasks.length > 12 ||
                    (leavePending?.length || 0) > 8 ||
                    reminders.length > 12 ||
                    unreadMessages.length > 12) && (
                    <p className="pt-1 text-[11px] text-slate-500">{moreListHint}</p>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </section>

      {showQaSection && qaGlance && (
        <div className={paneShell}>
          <div className={paneGlowA} aria-hidden />
          <div className={paneGlowB} aria-hidden />
          <div className="relative z-10 flex min-h-0 flex-1 flex-col">
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span
                  className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-indigo-100 ring-1 ring-white/20"
                >
                  <FileCheck className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-base font-bold tracking-tight text-white">
                    {translateUi("Your team's compliance")}
                  </h3>
                  <p className="mt-0.5 text-xs text-slate-300">
                    {qaScopeLabel}
                    {qaGlance.employeeCount > 0
                      ? translateUi(
                          qaGlance.employeeCount === 1
                            ? " · {{count}} employee checked"
                            : " · {{count}} employees checked",
                          { count: qaGlance.employeeCount }
                        )
                      : ""}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2.5 py-1 text-[11px] font-semibold text-emerald-200">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" aria-hidden /> {translateUi("Live")} </span>
              {canRunQAVerifications && (qaGlance.reporteeCount ?? 0) > 0 ? (
                <button
                  type="button"
                  onClick={() => setTeamQaOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 bg-white/10 px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-white/15"
                >
                  <Play className="h-3.5 w-3.5" />
                  {translateUi("Run all reportees")}
                  <span className="tabular-nums opacity-90">({qaGlance.reporteeCount})</span>
                </button>
              ) : null}
              <button
                type="button"
                onClick={() => onTab("qa-verifications")}
                className="text-xs font-semibold text-indigo-200 underline-offset-2 hover:text-white hover:underline"
              >
                {translateUi("Open QA Verifications")}
              </button>
            </div>
          </div>

          <div className={innerGrid}>
            <div className={innerPane}>
              <div className="flex min-h-0 flex-1 flex-col">
                <QaSeverityDonut counts={qaGlance.counts} isDarkMode />
              </div>
              <div className="mt-3 flex shrink-0 flex-wrap justify-center gap-2 border-t border-white/10 pt-3 text-sm font-semibold text-slate-300">
                <span>{translateUi("Compliant {{count}}", { count: qaGlance.counts.C })}</span>
                <span aria-hidden>·</span>
                <span className="text-amber-300">
                  {translateUi("Advisory {{count}}", { count: qaGlance.counts.A })}
                </span>
                <span aria-hidden>·</span>
                <span className="text-rose-300">
                  {translateUi("Review {{count}}", { count: qaGlance.counts.R })}
                </span>
              </div>
            </div>

            <div className={scrollList}>
              {qaGlance.issueCount === 0 ? (
                <div
                  className="flex h-full min-h-[8rem] items-start gap-3 rounded-xl border border-emerald-500/30 bg-emerald-950/20 px-4 py-5"
                >
                  <CheckCircle2
                    className="h-5 w-5 shrink-0 mt-0.5 text-emerald-300"
                  />
                  <div>
                    <p className="text-sm font-semibold text-emerald-100">
                      {translateUi("No live Review or Advisory issues")}
                    </p>
                    <p className="mt-1 text-xs text-emerald-200/80">
                      {qaGlance.scope === "company"
                        ? translateUi("Current records for the company look clear right now.")
                        : translateUi("Current records for your reportees look clear right now.")}
                    </p>
                  </div>
                </div>
              ) : (
                (qaGlance.issues || []).map((issue) => {
                  const critical = (issue.reviewCount || 0) > 0 || issue.overall === "fail";
                  return (
                    <button
                      key={`${issue.employeeId}-${issue.runId}`}
                      type="button"
                      onClick={() => openQaEmployee(issue.employeeId)}
                      className={cn(
                        "group w-full shrink-0 text-left rounded-xl border px-3.5 py-3 transition-colors flex items-start gap-3",
                        critical
                          ? "border-rose-500/40 bg-rose-950/35 hover:bg-rose-950/55"
                          : "border-amber-500/35 bg-amber-950/25 hover:bg-amber-950/45"
                      )}
                    >
                      <span
                        className={cn(
                          "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-white",
                          critical ? "bg-rose-600" : "bg-amber-500"
                        )}
                      >
                        <FileCheck className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span
                          className="block truncate text-sm font-semibold text-slate-50"
                        >
                          {issue.employeeName || "Employee"}
                        </span>
                        <span
                          className="mt-0.5 block text-xs text-slate-400"
                        >
                          {[
                            issue.reviewCount > 0
                              ? translateUi("Review {{count}}", { count: issue.reviewCount })
                              : null,
                            issue.advisoryCount > 0
                              ? translateUi("Advisory {{count}}", { count: issue.advisoryCount })
                              : null,
                            translateUi("Live check"),
                          ]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      </span>
                      <ChevronRight
                        className="mt-2 h-4 w-4 shrink-0 text-slate-500 transition-transform group-hover:translate-x-0.5"
                      />
                    </button>
                  );
                })
              )}
            </div>
          </div>
          </div>
        </div>
      )}

      {showManagerTeam && teamSummary && (
        <section className={paneShell}>
          <div className={paneGlowA} aria-hidden />
          <div className={paneGlowB} aria-hidden />
          <div className="relative z-10 flex min-h-0 flex-1 flex-col">
            <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/10 text-indigo-100 ring-1 ring-white/20">
                    <Users className="h-5 w-5" />
                  </span>
                  <div>
                    <h3 className="text-base font-bold tracking-tight text-white">{translateUi("My Team")}</h3>
                    <p className="mt-0.5 text-xs text-slate-300">{translateUi("Your assigned employees and their compliance position.")}</p>
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => onTab("reporting")}
                className="text-xs font-semibold text-indigo-200 underline-offset-2 hover:text-white hover:underline"
              >
                {translateUi("Open reporting")}
              </button>
            </div>
            <div className="mb-4 flex items-start gap-2 rounded-xl border border-sky-300/20 bg-sky-300/[0.08] px-3 py-2.5 text-xs text-sky-100">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-sky-200" />
              <p>
                <span className="font-semibold text-white">{translateUi("What does My Team mean?")}</span>{" "}
                {translateUi("These are employees assigned to you as their assessing manager.")}
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-white/15 bg-white/[0.06] p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">{translateUi("Compliance score")}</p>
                <p className={cn("mt-1 text-3xl font-bold tabular-nums", teamComplianceRate == null ? "text-slate-300" : teamComplianceRate >= 90 ? "text-emerald-300" : teamComplianceRate >= 70 ? "text-amber-300" : "text-rose-300")}>
                  {teamComplianceRate == null ? "—" : `${teamComplianceRate}%`}
                </p>
                <p className="mt-1 text-[11px] text-slate-400">{translateUi("Competencies currently in date")}</p>
              </div>
              <div className="rounded-xl border border-white/15 bg-white/[0.06] p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-300">{translateUi("Team size")}</p>
                <p className="mt-1 text-3xl font-bold tabular-nums text-white">{teamSummary.totalEmployees ?? 0}</p>
                <p className="mt-1 text-[11px] text-slate-400">{translateUi("assigned employees")}</p>
              </div>
              {[
                { label: translateUi("Overdue assessments"), value: teamSummary.overdueComp ?? 0, tone: "text-rose-300" },
                { label: translateUi("Assessments due soon"), value: teamSummary.dueSoonComp ?? 0, tone: "text-amber-300" },
                { label: translateUi("Licences expired"), value: teamSummary.licensing?.expired ?? 0, tone: "text-rose-300" },
                { label: translateUi("Licences due within 6 weeks"), value: teamSummary.licensing?.dueSoonSixWeeks ?? 0, tone: "text-amber-300" },
                { label: translateUi("Medicals expired"), value: teamSummary.expiredMed ?? 0, tone: "text-rose-300" },
                { label: translateUi("Medicals due within 30 days"), value: teamSummary.dueSoonMed ?? 0, tone: "text-amber-300" },
              ].map(({ label, value, tone }) => (
                <div key={label} className="flex items-center justify-between rounded-xl border border-white/15 bg-white/[0.06] px-3 py-2.5">
                  <span className="text-xs font-medium text-slate-300">{label}</span>
                  <span className={cn("text-xl font-bold tabular-nums", tone)}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
      </div>
    <TeamReporteeVerificationModal
      open={teamQaOpen}
      companyCode={companyCode}
      currentUserEmail={currentUserEmail}
      currentUserName={currentUserName}
      reporteeCount={reporteeCount}
      isDarkMode={isDarkMode}
      onClose={() => setTeamQaOpen(false)}
      onComplete={() => setQaRefreshKey((k) => k + 1)}
      onNavigateToFix={onNavigateToQaFix}
    />
    </>
  );
}
